import React from 'react';
import CoachAvatar from '../CoachAvatar';
import CoachMessageContent from './CoachMessageContent';
import { CoachActionCards } from '../CoachActionCard';
import { CoachVisibleMessage, DailyInsightCue } from './coachVisibleMessage';
import { CoachInlineTyping } from './coachInlineTyping';

/**
 * Loading placeholder for the feed's Coach slot. Covers two independent
 * gates that used to hide the entire card behind one monolithic skeleton:
 *
 *  - Identity unknown (profile hasn't settled): a robot/default avatar or
 *    name would be actively misleading, so this stays a compact, fully
 *    anonymous skeleton until the real identity is known.
 *  - Identity known but dojo membership hasn't settled: membership only
 *    decides DojoHub vs. the personal card, it has nothing to do with who
 *    the coach is. Render the known identity and authorized chat entry point;
 *    the canonical Hub owns context loading and submission readiness.
 */
const CoachFeedLoadingSlot = ({ contextLoading, aiCoachName, aiCoachEmoji, aiCoachImageUrl, coachMessage, backendService, hasAiFeature, openCoachChat, isTyping, dailyPending, children }) => {
  if (!contextLoading) {
    return (
      <div className="activity-slot-loading activity-coach-loading-slot" role="status" aria-label="Loading your coach">
        <div className="activity-slot-loading-header">
          <div className="activity-skeleton activity-skeleton-avatar compact" />
          <div className="activity-skeleton activity-skeleton-line coach-name" />
        </div>
      </div>
    );
  }

  const preview = Array.isArray(coachMessage?.paragraphs)
    ? coachMessage.paragraphs.filter(paragraph => typeof paragraph === 'string' && paragraph.trim()).map(paragraph => paragraph.trim())
    : [];

  return (
    <div
      className={`activity-slot-loading activity-coach-loading-slot context-loading ${dailyPending ? 'daily-insight-pending' : ''}`}
      aria-label={`${aiCoachName || 'Your coach'}`}
    >
      <div className="activity-slot-loading-header">
        <div className="activity-coach-loading-avatar">
          <CoachAvatar imageUrl={aiCoachImageUrl} emoji={aiCoachEmoji || '🥋'} identitySettled alt="" eager />
        </div>
        <span className="activity-coach-loading-name">{dailyPending ? 'Daily insight' : (aiCoachName || 'Coach')}</span>
      </div>
      {preview.map((paragraph, index) => (
        <CoachVisibleMessage
          key={`${coachMessage?.id || 'preview'}-${index}`}
          as="div"
          className="activity-coach-loading-preview daily-insight-ready"
          insightId={coachMessage?.focusInsightId}
          text={paragraph}
        >
          <CoachMessageContent text={paragraph} />
        </CoachVisibleMessage>
      ))}
      <CoachActionCards actions={coachMessage?.actions} backendService={backendService} />
      {dailyPending && hasAiFeature && <DailyInsightCue />}
      {isTyping && hasAiFeature && <CoachInlineTyping name={aiCoachName} />}
      {children}
      {hasAiFeature && openCoachChat && (
        <div className="dojo-hub-coach-cta">
          <button type="button" className="dojo-hub-chat-btn" onClick={() => openCoachChat()}>
            💬 Chat with {aiCoachName || 'your coach'} →
          </button>
        </div>
      )}
    </div>
  );
};

export default CoachFeedLoadingSlot;
